import { useState } from 'react';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { box, button, input, label } from '../shared/styles';

/*
 * A started experiment's units, with their plates (migration 0045) and flasks
 * (0050). The table stays once the experiment has ended; what changes it is
 * offered only while it runs.
 *
 *  - "Make plates": one plate per unit that has none, coded like every other
 *    plate and linked to its unit. Asking again makes nothing new. The code
 *    of what they are inoculated from (a slant, plate or strain) goes with
 *    them as the plates' source.
 *  - "Add flask": an Erlenmeyer for one unit, registered through POST
 *    /vessels, made from the code typed or scanned into "Made from" (a
 *    plate, slant, flask or strain), else from the unit's plate. Made from a
 *    plate or slant it is coded {that code}-E{n}; otherwise the station's
 *    own VES- code.
 *  - "Carry plate checks into an outcome": the values people recorded on the
 *    units' plates become observations, each naming the check it came from.
 *    Only fields that fit the outcome's type are offered; a unit whose plate
 *    has no check is listed, never filled.
 *
 * Naming the source is what puts the specimen (ISO-...) into the experiment's
 * record: a plate coded P{n} or a flask coded VES-... carries no specimen in
 * its code, and the record finds it only through what they were made from.
 */
export const ROLLUP_FIELDS = {
  continuous: ['colony_diameter_mm', 'ph'],
  binary: ['contamination'],
  ordinal: ['sporulation_stage'],
  count: [],
};

// The field of POST /vessels that each kind of source goes in.
export const FLASK_SOURCE = {
  plate: 'parent_plate_id', slant: 'parent_slant_id', vessel: 'parent_vessel_id', strain: 'strain_id',
};
// What POST /experiments/{id}/plates takes as an inoculum.
export const PLATE_SOURCE = ['plate', 'slant', 'strain'];

/*
 * What a scanned or typed code names, as the source of a culture, through
 * GET /scan/{code}, which reads any label in the lab (vessels included,
 * which /lookup does not). A specimen (ISO-...) counts through its line, its
 * strain. Anything outside `kinds`, or a specimen with no strain, is null.
 */
export async function resolveSource(code, kinds) {
  const { data } = await api.get(`/scan/${encodeURIComponent(code)}`);
  if (data.kind === 'specimen' && data.details?.strain_id && kinds.includes('strain')) {
    return { kind: 'strain', id: data.details.strain_id, code: data.code };
  }
  return kinds.includes(data.kind) ? { kind: data.kind, id: data.id, code: data.code } : null;
}

const NO_FLASK = { source: '', medium: '', volume: '', location: '', notes: '' };

function AddFlask({ unit, onAdded, t }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(NO_FLASK);
  const [minted, setMinted] = useState('');
  const [error, setError] = useState('');
  const id = `${unit.arm}-${unit.replicate}`;
  const field = (key) => ({ value: form[key], onChange: (e) => setForm({ ...form, [key]: e.target.value }) });
  const text = (value) => value.trim() || null;

  const save = async () => {
    setError('');
    const volume = form.volume.trim() === '' ? null : Number(form.volume);
    if (volume !== null && !(volume > 0)) {
      setError(t('volumeInvalid'));
      return;
    }
    try {
      let parent = unit.plate_id ? { parent_plate_id: unit.plate_id } : {};
      const code = form.source.trim();
      if (code) {
        const found = await resolveSource(code, Object.keys(FLASK_SOURCE));
        if (!found) {
          setError(t('sourceNotFlask', { code }));
          return;
        }
        parent = { [FLASK_SOURCE[found.kind]]: found.id };
      }
      const { data } = await api.post('/vessels', {
        kind: 'flask', experiment_unit_id: unit.unit_id, ...parent,
        medium: text(form.medium), volume_ml: volume, location: text(form.location), notes: text(form.notes),
      });
      setMinted(data.code);
      setForm(NO_FLASK);
      setOpen(false);
      onAdded();
    } catch (e) { setError(errorText(e)); }
  };

  return (
    <div style={{ marginTop: 4 }}>
      {!open && (
        <button type="button" onClick={() => { setOpen(true); setMinted(''); }} title={t('tip.addFlask')}
          data-testid={`add-flask-${id}`}>{t('addFlask')}</button>
      )}
      {open && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={label}>{t('madeFrom')}
            <input style={input} dir="ltr" {...field('source')} placeholder={unit.plate_code || 'ISO-…-SA'}
              title={t('tip.flaskSource')} data-testid={`flask-source-${id}`} />
          </label>
          <label style={label}>{t('medium')}
            <input style={input} {...field('medium')} placeholder="PDB 24 g/L" title={t('tip.flaskMedium')}
              data-testid={`flask-medium-${id}`} />
          </label>
          <label style={label}>{t('volumeMl')}
            <input style={{ ...input, width: 90 }} inputMode="decimal" {...field('volume')} title={t('tip.flaskVolume')}
              data-testid={`flask-volume-${id}`} />
          </label>
          <label style={label}>{t('location')}
            <input style={input} {...field('location')} title={t('tip.flaskLocation')} data-testid={`flask-location-${id}`} />
          </label>
          <label style={label}>{t('flaskNotes')}
            <input style={input} {...field('notes')} title={t('tip.flaskNotes')} data-testid={`flask-notes-${id}`} />
          </label>
          <button type="button" style={button('var(--c-primary)')} onClick={save} title={t('tip.saveFlask')}
            data-testid={`flask-save-${id}`}>{t('saveFlask')}</button>
          <button type="button" onClick={() => { setOpen(false); setError(''); }} title={t('tip.cancelFlask')}>
            {t('cancel')}</button>
        </div>
      )}
      {minted && <span style={{ fontSize: 12 }} data-testid={`flask-minted-${id}`}>{t('flaskAdded', { code: minted })}</span>}
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)', margin: 0 }}>{error}</p>}
    </div>
  );
}

export default function PlatesPanel({ detail, running, canWrite, canObserve, onChanged, t }) {
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [inoculum, setInoculum] = useState('');
  const rollable = detail.outcomes.filter((o) => (ROLLUP_FIELDS[o.type] || []).length);
  const [outcome, setOutcome] = useState(rollable[0]?.key || '');
  const type = detail.outcomes.find((o) => o.key === outcome)?.type;
  const fields = ROLLUP_FIELDS[type] || [];
  const [field, setField] = useState(fields[0] || '');
  const unplated = detail.units.filter((u) => !u.plate_id).length;
  const flasksOf = {};
  (detail.vessels || []).forEach((v) => { (flasksOf[v.unit_id] ||= []).push(v.code); });

  const makePlates = async () => {
    setError(''); setMessage('');
    try {
      const code = inoculum.trim();
      let body = {};
      if (code) {
        const found = await resolveSource(code, PLATE_SOURCE);
        if (!found) {
          setError(t('sourceNotPlate', { code }));
          return;
        }
        body = { inoculum: { kind: found.kind, id: found.id } };
      }
      const { data } = await api.post(`/experiments/${detail.experiment_id}/plates`, body);
      setInoculum('');
      setMessage(t('platesMade', { count: data.created }));
      onChanged();
    } catch (e) { setError(errorText(e)); }
  };

  const rollup = async () => {
    setError(''); setMessage('');
    try {
      const { data } = await api.post(`/experiments/${detail.experiment_id}/observations/from-plate-checks`,
        { outcome, field: fields.includes(field) ? field : fields[0] });
      setMessage(t('rolledUp', { imported: data.imported, already: data.already })
        + (data.missing.length ? ` ${t('rollupMissing', { units: data.missing.join(', ') })}` : ''));
      onChanged();
    } catch (e) { setError(errorText(e)); }
  };

  return (
    <section style={box} data-testid="plates-panel">
      <h4 style={{ marginTop: 0 }}>{t('unitsTitle')}</h4>
      <table style={{ fontSize: 13, borderCollapse: 'collapse', marginBottom: 8 }}>
        <thead><tr><th style={{ textAlign: 'start', paddingInlineEnd: 12 }}>{t('unit')}</th>
          <th style={{ textAlign: 'start', paddingInlineEnd: 12 }}>{t('plate')}</th>
          <th style={{ textAlign: 'start' }}>{t('flasks')}</th></tr></thead>
        <tbody>{detail.units.map((u) => (
          <tr key={u.unit_id} style={{ verticalAlign: 'top' }}><td style={{ paddingInlineEnd: 12 }}>{u.label}</td>
            <td style={{ paddingInlineEnd: 12 }} data-testid={`plate-of-${u.arm}-${u.replicate}`}>{u.plate_code || '—'}</td>
            <td>
              <span data-testid={`flasks-of-${u.arm}-${u.replicate}`}>{(flasksOf[u.unit_id] || []).join(', ') || '—'}</span>
              {running && canObserve && <AddFlask unit={u} onAdded={onChanged} t={t} />}
            </td></tr>
        ))}</tbody>
      </table>
      {running && canWrite && unplated > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={label}>{t('inoculatedFrom')}
            <input style={input} dir="ltr" value={inoculum} onChange={(e) => setInoculum(e.target.value)}
              placeholder="ISO-…-SA" title={t('tip.inoculum')} data-testid="inoculum-code" />
          </label>
          <button type="button" style={button('var(--c-primary)')} onClick={makePlates} title={t('tip.makePlates')} data-testid="make-plates">
            {t('makePlates', { count: unplated })}</button>
        </div>
      )}
      {running && canObserve && rollable.length > 0 && detail.units.some((u) => u.plate_id) && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 10 }}>
          <label style={label}>{t('rollupOutcome')}
            <select style={input} value={outcome} title={t('tip.rollupOutcome')} data-testid="rollup-outcome"
              onChange={(e) => { setOutcome(e.target.value); setField(''); }}>
              {rollable.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </label>
          <label style={label}>{t('rollupField')}
            <select style={input} value={fields.includes(field) ? field : fields[0]} title={t('tip.rollupField')} data-testid="rollup-field"
              onChange={(e) => setField(e.target.value)}>
              {fields.map((f) => <option key={f} value={f}>{t(`field_${f}`)}</option>)}
            </select>
          </label>
          <button type="button" style={button('var(--c-primary)')} onClick={rollup} title={t('tip.rollup')} data-testid="rollup">
            {t('rollup')}</button>
        </div>
      )}
      {message && <p data-testid="plates-message" style={{ fontSize: 13 }}>{message}</p>}
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
    </section>
  );
}
